import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import FilterDrawer from "components/filter_form/filter_drawer/FilterDrawer";
import Button from "components/ui/button/Button";
import Badge from "lib/core_components/Badge";
import {
  CognismContactFilters,
  CONTACT_FILTER_GROUP_KEYS,
  CONTACT_FILTER_GROUP_RESETS,
  ContactFilterGroupKey,
  countActiveFiltersForGroups,
} from "./cognismContactFilters";
import { CognismContactsFilterSections } from "./CognismContactsFilterPanel";

export type CognismContactsFilterDrawerProps = {
  open: boolean;
  onClose: () => void;
  filters: CognismContactFilters;
  onChange: (filters: CognismContactFilters) => void;
  /** Sections to omit (embedded surfaces lock the buyer scope and search inline). */
  hiddenSections?: ContactFilterGroupKey[];
};

/**
 * Drawer shell around the shared contacts filter sections, for embedded surfaces
 * (matches the Filters-button-plus-drawer pattern used by other buyer-profile tabs).
 * Counts and "Clear all" only cover the visible sections — hidden ones (e.g. the
 * locked buyer scope) are never user state here.
 */
function CognismContactsFilterDrawer({
  open,
  onClose,
  filters,
  onChange,
  hiddenSections = [],
}: CognismContactsFilterDrawerProps) {
  const visibleGroups = CONTACT_FILTER_GROUP_KEYS.filter((key) => !hiddenSections.includes(key));
  const activeCount = countActiveFiltersForGroups(filters, visibleGroups);

  const clearVisible = () => {
    const resets = visibleGroups.reduce<Partial<CognismContactFilters>>(
      (next, group) => ({ ...next, ...CONTACT_FILTER_GROUP_RESETS[group] }),
      {},
    );
    onChange({ ...filters, ...resets });
  };

  return (
    <FilterDrawer open={open} onClose={onClose}>
      {open && (
        <Flex column aria-label="Contacts filters drawer">
          <DrawerHeader>
            <Flex alignItems="center" gap={8}>
              <HeaderTitle>Filters</HeaderTitle>
              <Badge size={18}>{activeCount}</Badge>
              {activeCount > 0 && (
                <Button buttonType="tertiary" onClick={clearVisible}>
                  Clear all
                </Button>
              )}
            </Flex>
            <Button
              buttonType="secondary"
              icon="chevronRightDouble"
              aria-label="Close filters"
              onClick={onClose}
            />
          </DrawerHeader>
          <CognismContactsFilterSections
            filters={filters}
            onChange={onChange}
            hiddenSections={hiddenSections}
          />
        </Flex>
      )}
    </FilterDrawer>
  );
}

// Mirrors the sidebar panel's header so both shells read identically.
const DrawerHeader = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "16px 24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  height: 64,
}));

const HeaderTitle = styled.div(({ theme }) => ({
  color: theme.colors.sysTextDefault,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "125%",
}));

export default CognismContactsFilterDrawer;
